import { motion } from 'framer-motion';
import { SectionCard, Field, MultiSelect, RadioSelect, inputClass, textareaClass } from '@/components/SurveyComponents';
import type { SurveyData } from '@/lib/index';

interface Props { data: SurveyData; update: (d: Partial<SurveyData>) => void; }

const RESTRICTIONS = ['None — we eat everything','Vegetarian','Vegan','Gluten-free','Dairy-free','Halal','Kosher','Nut allergy','Shellfish allergy','Other (I will note below)'];
const MEAL_VIBES = ['Private chef dinner at the villa or rental','Group dinner at a top restaurant','Street food and local spots','Food tours and tastings','Cooking class together','Delivery / low-key nights in','Rooftop or view restaurants','Breakfast spot finds','Brunch is a religious experience for us'];
const MEAL_SPLITS = ['Every meal together as a group','Most dinners together, breakfast/lunch on our own','Just a few group meals — mostly independent','Completely on our own schedule'];

export function Step6Dining({ data, update }: Props) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
      <div className="text-center mb-4">
        <h2 className="font-serif text-2xl font-bold text-foreground">Dining Preferences 🍷</h2>
        <p className="text-muted-foreground text-sm mt-1">Good food is non-negotiable. Tell me how your group eats.</p>
      </div>
      <SectionCard title="Dietary Needs" emoji="🌱">
        <Field label="Any dietary restrictions or allergies in the group?" hint="Select all that apply">
          <MultiSelect cols={2} options={RESTRICTIONS} selected={data.dietaryRestrictions} onChange={v => update({ dietaryRestrictions: v })} />
        </Field>
        {data.dietaryRestrictions?.includes('Other (I will note below)') && (
          <Field label="Please describe any other dietary needs">
            <input className={inputClass} placeholder="Describe anything not listed above..." value={data.dietaryOther} onChange={e => update({ dietaryOther: e.target.value })} />
          </Field>
        )}
      </SectionCard>
      <SectionCard title="Dining Experiences" emoji="🍽️">
        <Field label="What kind of dining experiences do you want on this trip?" hint="Select your favorites">
          <MultiSelect cols={1} options={MEAL_VIBES} selected={data.diningExperiences} onChange={v => update({ diningExperiences: v })} />
        </Field>
        <Field label="How often does the group want to eat together?">
          <RadioSelect cols={1} options={MEAL_SPLITS} value={data.mealStructure} onChange={v => update({ mealStructure: v })} />
        </Field>
        <Field label="Cuisine preferences?" hint="Any must-have cuisines or types of food">
          <input className={inputClass} placeholder="e.g. Mexican, Japanese, Mediterranean, farm-to-table..." value={data.cuisinePrefs} onChange={e => update({ cuisinePrefs: e.target.value })} />
        </Field>
        <Field label="Dining budget per person per meal?">
          <RadioSelect cols={2} options={['$15–$30 (street food / casual)','$30–$60 (mid-range)','$60–$120 (elevated dining)','$120–$200 (fine dining)','Mix of all levels','Whatever it takes for great food']} value={data.diningBudget} onChange={v => update({ diningBudget: v })} />
        </Field>
      </SectionCard>
      <SectionCard title="Drinks & Extras" emoji="🥂">
        <Field label="Drink preferences for group meals?">
          <MultiSelect cols={2} options={['Wine pairings','Craft cocktails','Local spirits and specialties','Beer / chill drinks','Non-alcoholic / mocktail options','Whatever the vibe calls for']} selected={data.drinkPrefs} onChange={v => update({ drinkPrefs: v })} />
        </Field>
        <Field label="Would you like a private chef experience included?" hint="A private chef cooks at your villa or rental — very popular for group trips">
          <RadioSelect cols={2} options={['Yes — at least one night','Multiple nights please','Only if it fits the budget','No thank you','Tell me more about this option']} value={data.privateChef} onChange={v => update({ privateChef: v })} />
        </Field>
        <Field label="Anything else about food or dining we should know?">
          <textarea className={textareaClass} placeholder="Food allergies, a bucket-list restaurant, a cuisine you are obsessed with..." value={data.diningNotes} onChange={e => update({ diningNotes: e.target.value })} />
        </Field>
      </SectionCard>
    </motion.div>
  );
}
